import type { EventEntityDefinition } from '@gzl10/nexus-sdk'
import {
  useIdField,
  useTextField,
  useSelectField,
  useTextareaField,
  useJsonField,
  useDatetimeField,
  useEmailField
} from '@gzl10/nexus-sdk/fields'

/**
 * Generic audit log — immutable, append-only.
 * Consumed by any module/plugin via AuditService.
 * Retention: 90 days by default.
 */
export const auditLogEntity: EventEntityDefinition = {
  type: 'event',
  immutable: true,
  table: 'audit_log',
  label: { en: 'Audit Log', es: 'Registro de auditoría' },
  labelPlural: { en: 'Audit Logs', es: 'Registros de auditoría' },
  labelField: 'action',
  routePrefix: '/log',
  retention: { days: 90 },
  calendarFrom: 'created_at',

  fields: {
    id: useIdField(),
    source: {
      ...useTextField({
        label: { en: 'Source', es: 'Origen' },
        size: 100,
        nullable: false,
        index: true,
        meta: { sortable: true, searchable: true }
      }),
      validation: { min: 1, max: 100 }
    },
    action: {
      ...useTextField({
        label: { en: 'Action', es: 'Acción' },
        size: 100,
        nullable: false,
        index: true,
        meta: { sortable: true, searchable: true }
      }),
      validation: { min: 1, max: 100 }
    },
    actor_id: useSelectField({
      label: { en: 'Actor', es: 'Actor' },
      table: 'users',
      column: 'id',
      onDelete: 'SET NULL',
      endpoint: '/users',
      valueField: 'id',
      labelField: 'name',
      nullable: true,
      index: true,
      meta: { searchable: true }
    }),
    actor_email: useEmailField({
      label: { en: 'Actor Email', es: 'Email del actor' },
      nullable: true,
      meta: { searchable: true }
    }),
    resource_type: useTextField({
      label: { en: 'Resource Type', es: 'Tipo de recurso' },
      size: 100,
      nullable: true,
      index: true,
      meta: { searchable: true }
    }),
    resource_id: useTextField({
      label: { en: 'Resource ID', es: 'ID del recurso' },
      size: 100,
      nullable: true,
      meta: { searchable: true }
    }),
    ip_address: useTextField({
      label: { en: 'IP Address', es: 'Dirección IP' },
      size: 45,
      nullable: true,
      meta: { searchable: true }
    }),
    user_agent: useTextareaField({
      label: { en: 'User Agent', es: 'Agente de usuario' },
      nullable: true,
      meta: { exportable: false }
    }),
    metadata: useJsonField({
      label: { en: 'Metadata', es: 'Metadatos' },
      nullable: true,
      meta: { exportable: false }
    }),
    created_at: useDatetimeField({
      label: { en: 'Date', es: 'Fecha' },
      disabled: true,
      nullable: false,
      meta: { sortable: true }
    })
  },

  casl: {
    subject: 'AuditLog',
    permissions: {
      SUPPORT: { actions: ['read'] }
    }
  }
}
